/**
 * Geo Popup Component
 * Handles geo-location detection and store selection popup
 */
class GeoPopup {
  constructor(element) {
    this.element = element;
    this.config = JSON.parse(element.querySelector('[data-geo-config]').textContent);
    this.dismissButtons = element.querySelectorAll('[data-dismiss]');


    // Storage key for user's geo selection
    this.storageKey = 'geo-selection';
    this.storageExpiry = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    this.aggressiveStorageExpiry = 15 * 60 * 1000; // 15 minutes for aggressive countries
    this.aggressiveCountries = ['US'];

    this.init();
  }

  init() {
    // Add event listeners for dismiss buttons
    this.dismissButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // If it's a current store link, prevent navigation
        if (button.href && button.href.includes('javascript:void(0)')) {
          e.preventDefault();
        }
        this.hide();
      });
    });

    // Add overlay click to dismiss
    const overlay = this.element.querySelector('.geo-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.hide());
    }

    // Check if popup should be shown
    this.checkAndShow();
  }

  async checkAndShow() {
    // ?geo parameter = user was just redirected here, skip all geo logic to prevent loops
    if (this.hasGeoParam()) {
      return;
    }

    try {
      const userCountryCode = await this.getUserLocation();

      if (!userCountryCode) return;

      // Show popup if dismissed recently, skip (unless aggressive country)
      if (this.isRecentlyDismissed(userCountryCode)) {
        return;
      }

      // Highlight recommended store based on user's location
      this.highlightRecommendedStore(userCountryCode);

      if (this.shouldShowPopup(userCountryCode)) {
        // Small delay to ensure page is loaded
        setTimeout(() => this.show(), 1000);
      }
    } catch (error) {
      console.warn('Could not determine user location:', error);
    }
  }

  hasGeoParam() {
    return new URLSearchParams(window.location.search).has('geo');
  }

  isRecentlyDismissed(userCountryCode) {
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return false;

    try {
      const { timestamp } = JSON.parse(stored);
      const now = Date.now();
      const isAggressive = this.aggressiveCountries.includes(userCountryCode);
      const expiry = isAggressive ? this.aggressiveStorageExpiry : this.storageExpiry;
      return (now - timestamp) < expiry;
    } catch {
      // Invalid stored data, clear it
      localStorage.removeItem(this.storageKey);
      return false;
    }
  }

  async getUserLocation() {
    try {
      // Try multiple geolocation services for reliability
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://api.country.is/'
      ];

      for (const service of services) {
        try {
          const response = await fetch(service, { timeout: 5000 });
          if (!response.ok) continue;

          const data = await response.json();

          // Different services have different response formats
          let countryCode = data.country_code || data.countryCode || data.country;

          if (countryCode) {
            return countryCode.toUpperCase();
          }
        } catch (serviceError) {
          console.warn(`Geolocation service ${service} failed:`, serviceError);
          continue;
        }
      }

      throw new Error('All geolocation services failed');
    } catch (error) {
      console.warn('Could not determine user location:', error);
      return null;
    }
  }

  shouldShowPopup(userCountryCode) {
    if (!userCountryCode) return false;

    const currentStore = this.config.current;

    // Check if user's country matches current store's primary country
    if (currentStore.code === userCountryCode) {
      return false;
    }

    // Check if user's country is in current store's redirect list
    if (currentStore.redirects && currentStore.redirects.includes(userCountryCode)) {
      return false;
    }

    // Check if user should be redirected to a different store
    const targetStore = this.findTargetStore(userCountryCode);
    if (targetStore) {
      // Could auto-redirect here, but showing popup for user choice
      return true;
    }

    // Show popup for international users or unmatched countries
    return true;
  }

  findTargetStore(userCountryCode) {
    return this.config.stores.find(store => {
      return store.code === userCountryCode ||
             (store.redirects && store.redirects.includes(userCountryCode));
    });
  }

  highlightRecommendedStore(userCountryCode) {
    const options = this.element.querySelectorAll('.geo-popup__option');

    options.forEach(option => {
      const optionCode = option.getAttribute('data-code');
      const optionRedirects = option.getAttribute('data-redirects');
      const isCurrent = option.getAttribute('data-current') === 'true';

      // Check if this option matches the user's country
      const isMatch = optionCode === userCountryCode ||
                     (optionRedirects && optionRedirects.split(',').includes(userCountryCode));

      if (isMatch && !isCurrent) {
        option.classList.add('geo-popup__option--recommended');

        // Add "Recommended" badge
        if (!option.querySelector('.geo-popup__badge')) {
          const badge = document.createElement('span');
          badge.className = 'geo-popup__badge ds-caption';
          badge.textContent = 'Recommended';
          option.appendChild(badge);
        }
      }
    });
  }

  show() {
    this.element.hidden = false;
    document.body.style.overflow = 'hidden';

    // Focus management for accessibility
    this.trapFocus();
  }

  hide() {
    this.element.hidden = true;
    document.body.style.overflow = '';

    // Remember user's choice with timestamp
    const dismissData = {
      timestamp: Date.now(),
      dismissed: true
    };
    localStorage.setItem(this.storageKey, JSON.stringify(dismissData));

    // Remove focus trap
    this.removeFocusTrap();
  }

  trapFocus() {
    const modal = this.element.querySelector('.geo-popup__modal');
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus the first element
    firstElement.focus();

    this.focusHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', this.focusHandler);
  }

  removeFocusTrap() {
    if (this.focusHandler) {
      document.removeEventListener('keydown', this.focusHandler);
      this.focusHandler = null;
    }
  }

  // Method to manually show popup (can be called from other components)
  async forceShow() {
    // Detect and highlight recommended store if not already done
    if (!this.element.querySelector('.geo-popup__option--recommended')) {
      try {
        const userCountryCode = await this.getUserLocation();
        if (userCountryCode) {
          this.highlightRecommendedStore(userCountryCode);
        }
      } catch (error) {
        console.warn('Could not determine user location:', error);
      }
    }
    this.show();
  }

  // Method to clear stored selection (useful for testing)
  clearSelection() {
    localStorage.removeItem(this.storageKey);
  }
}

// Initialize geo popup (script runs with defer, so DOM is already loaded)
const geoPopupElement = document.querySelector('[data-component="geo-popup"]');
if (geoPopupElement) {
  window.geoPopup = new GeoPopup(geoPopupElement);
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GeoPopup;
}